// SB6-ANA-FILT-06 (Track C) — AI Filter Module.
//
// Advanced AI-side filtering, opened from the L3 Kind "AI" button's
// sliders icon (D4) or from Settings → Layout & Widgets. Built on the
// generic `FacetTableWidget` primitive (S1):
//
//   ☑ | AI class name | confidence (histogram + slider + numeric) | # ≥ threshold
//
//   * Rows come from `GET …/{id}/ai-class-facets` (L1+L2-scoped — we pass
//     the global filter's status + reviewState; `keepPreviousData`).
//   * Each row's confidence column is a single-thumb slider (1..99,
//     default 35 — D7) with a synced integer numeric field and the class's
//     confidence histogram rendered BEHIND the slider track (S3).
//   * "# findings ≥ threshold" is computed CLIENT-SIDE from the cumulative
//     histogram (D8): dragging the slider updates instantly with NO
//     request per tick.
//   * A "Show all classes" toggle refetches with includeEmpty.
//   * Per-class checked + per-class minConfidencePct form the widget
//     config (shared `aiFilterStore`).
//   * The "Filter active" header switch is two-way synced with the L3 AI
//     option button (D4) via the shared `kind` axis (S8 — single source of
//     truth; the same pattern as Track B's Defect widget).
//   * On COMMIT (filter active + config change settled — the commit alone
//     is debounced 300ms; the live count is debounce-free) the widget
//     pushes the `aiClassFilter` JSON map `{ "<classUuid>": <minPct> }`
//     over the CHECKED classes into the shared store, which the global
//     data-point + facets queries fold into their requests so the BE
//     applies it and the L3 AI count + cascade reflect the AI config.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  InputBase,
  Slider,
  Stack,
  Switch,
} from "@mui/material";
import type { AiClassFacet, ConfidenceBucket } from "@aeriosiq/types";
import { useAiClassFacetsQuery } from "../../queries.js";
import { useDataPointFilter } from "../../useDataPointFilter.js";
import {
  AI_MAX_CONFIDENCE,
  AI_MIN_CONFIDENCE,
  DEFAULT_AI_MIN_CONFIDENCE_PCT,
  buildAiClassFilterJson,
  clampConfidencePct,
  useAiFilterStore,
  type AiClassConfig,
} from "../../state/aiFilterStore.js";
import {
  FacetTableWidget,
  type FacetExtraColumn,
  type FacetRow,
} from "./FacetTableWidget.js";
import { FilterConfigPresets } from "./FilterConfigPresets.js";
import { useWidgetFilterConfigs } from "../../useWidgetFilterConfigs.js";
import {
  buildAiConfigBlob,
  gateAiConfigBlob,
  parseAiConfigBlob,
} from "../../filterConfigBlob.js";

interface AiFilterWidgetProps {
  dataAcquisitionId: string;
}

const AI_KIND = "ai";

/** Debounce window for the COMMIT only (D8). */
const COMMIT_DEBOUNCE_MS = 300;

/** Pick a localized label, preferring `en`, then `de`, then any value. */
function localized(name: Record<string, string>): string {
  return name.en ?? name.de ?? Object.values(name)[0] ?? "";
}

interface AiRow extends FacetRow {
  classUuid: string;
  histogram: ConfidenceBucket[];
  findingCount: number;
}

/**
 * Client-side "# findings ≥ threshold" (D8). Sums the count of every
 * histogram bucket whose lower edge `bucketPct` is at/above the threshold
 * — the cumulative tail of the distribution. Pure function so the unit
 * test can assert it against a known histogram with no React.
 */
export function countAtOrAboveThreshold(
  histogram: readonly ConfidenceBucket[],
  thresholdPct: number,
): number {
  let total = 0;
  for (const b of histogram) {
    if (b.bucketPct >= thresholdPct) total += b.count;
  }
  return total;
}

/**
 * The per-class confidence control: the class's confidence histogram
 * drawn behind a single-thumb slider (1..99) with a synced integer
 * numeric field. Editing either keeps both in lock-step (D7). All values
 * are integer percent — the numeric field rejects decimals.
 */
function ConfidenceControl({
  classUuid,
  histogram,
  value,
  onChange,
}: {
  classUuid: string;
  histogram: readonly ConfidenceBucket[];
  value: number;
  onChange: (next: number) => void;
}): React.ReactElement {
  const maxBucket = histogram.reduce((m, b) => Math.max(m, b.count), 0);
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ minWidth: 200 }}
      data-testid={`ai-filter-confidence-${classUuid}`}
    >
      <Box sx={{ position: "relative", flexGrow: 1, minWidth: 120 }}>
        {/* Histogram behind the track (S3). Bars proportional to bucket
            counts; purely decorative so aria-hidden. */}
        <Box
          aria-hidden
          data-testid={`ai-filter-histogram-${classUuid}`}
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "flex-end",
            gap: "1px",
            opacity: 0.35,
            pointerEvents: "none",
          }}
        >
          {histogram.map((b) => (
            <Box
              key={b.bucketPct}
              data-testid={`ai-filter-hist-bar-${classUuid}-${String(b.bucketPct)}`}
              sx={{
                flexGrow: 1,
                flexBasis: 0,
                height:
                  maxBucket > 0
                    ? `${String(Math.max(2, Math.round((b.count / maxBucket) * 100)))}%`
                    : "2%",
                backgroundColor: "primary.main",
                borderRadius: "1px 1px 0 0",
              }}
            />
          ))}
        </Box>
        <Slider
          size="small"
          min={AI_MIN_CONFIDENCE}
          max={AI_MAX_CONFIDENCE}
          value={value}
          onChange={(_e, v) => {
            onChange(
              clampConfidencePct(Array.isArray(v) ? (v[0] ?? value) : v),
            );
          }}
          aria-label={`Minimum confidence for ${classUuid}`}
          data-testid={`ai-filter-slider-${classUuid}`}
          sx={{ mx: 0.5 }}
        />
      </Box>
      <InputBase
        type="number"
        value={String(value)}
        onChange={(e) => {
          const raw = Number.parseInt(e.target.value, 10);
          if (Number.isNaN(raw)) return;
          onChange(clampConfidencePct(raw));
        }}
        inputProps={{
          min: AI_MIN_CONFIDENCE,
          max: AI_MAX_CONFIDENCE,
          step: 1,
          "aria-label": `Minimum confidence percent for ${classUuid}`,
          "data-testid": `ai-filter-numeric-${classUuid}`,
          style: { width: 40, textAlign: "right" },
        }}
        endAdornment={
          <Box component="span" sx={{ pl: 0.25, color: "text.secondary" }}>
            %
          </Box>
        }
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          px: 0.5,
          fontVariantNumeric: "tabular-nums",
        }}
      />
    </Stack>
  );
}

export function AiFilterWidget({
  dataAcquisitionId,
}: AiFilterWidgetProps): React.ReactElement {
  const { selection, setAxis } = useDataPointFilter();
  // ANA-R1-05 (AC4) — default to the FULL class list. Present-mode (showAll
  // false) scopes class MEMBERSHIP to data points that match the current
  // ACTIVE-L1/L2 selection, so a class whose findings live only on
  // status-filtered-out data points silently vanishes from the list (the
  // walkthrough's "this class is even not in the list"). Listing every class
  // with ≥1 finding in the dataset by default — independent of the current
  // confidence/status narrowing — restores completeness. The per-row COUNT
  // column stays L1+L2-scoped (that narrowing is correct and useful); only the
  // row's PRESENCE is no longer gated. The "Show all classes" toggle is now an
  // inverse "narrow to present classes" affordance the analyst can still flip.
  const [showAll, setShowAll] = useState(true);

  const aiFacetsQuery = useAiClassFacetsQuery(dataAcquisitionId, selection, {
    includeEmpty: showAll,
  });
  const { data, isPending, isError } = aiFacetsQuery;

  // SB6-ANA-PROG-03 — per-widget refetch indication is gone (the bottom
  // status overlay + the FilterWidget aggregate cover "something is
  // updating"). The widget still keeps its true FIRST-LOAD spinner
  // (`isPending` → the centered spinner in `emptyMessage`); a refetch for a
  // newer revision simply keeps the previous rows full-contrast (the
  // `keepPreviousData` placeholder), no dimming.

  const config = useAiFilterStore((s) => s.config);
  const setClass = useAiFilterStore((s) => s.setClass);
  const setConfig = useAiFilterStore((s) => s.setConfig);
  const setCommittedFilter = useAiFilterStore((s) => s.setCommittedFilter);

  const facetItems: AiClassFacet[] = useMemo(() => data?.items ?? [], [data]);

  // Resolve a class's config, defaulting unseen classes to unchecked at
  // the default 35% threshold (D7) without mutating the store on render.
  const cfgFor = useCallback(
    (classUuid: string): AiClassConfig =>
      config[classUuid] ?? {
        checked: false,
        minPct: DEFAULT_AI_MIN_CONFIDENCE_PCT,
      },
    [config],
  );

  const rows: AiRow[] = useMemo(
    () =>
      facetItems.map((it) => ({
        id: it.classUuid,
        classUuid: it.classUuid,
        name: localized(it.className),
        // The count column shows the class's full L1+L2-scoped population;
        // the live "# ≥ threshold" is the extra column (D8).
        count: it.findingCount,
        findingCount: it.findingCount,
        histogram: it.confidenceHistogram,
      })),
    [facetItems],
  );

  const checkedIds = useMemo(() => {
    const s = new Set<string>();
    for (const [uuid, c] of Object.entries(config)) {
      if (c.checked) s.add(uuid);
    }
    return s;
  }, [config]);

  const toggleRow = useCallback(
    (id: string, checked: boolean) => {
      setClass(id, { ...cfgFor(id), checked });
    },
    [cfgFor, setClass],
  );

  const toggleAll = useCallback(
    (checked: boolean) => {
      const next = { ...config };
      for (const r of rows) {
        next[r.classUuid] = { ...cfgFor(r.classUuid), checked };
      }
      setConfig(next);
    },
    [config, rows, cfgFor, setConfig],
  );

  const setMinPct = useCallback(
    (id: string, minPct: number) => {
      setClass(id, { ...cfgFor(id), minPct: clampConfidencePct(minPct) });
    },
    [cfgFor, setClass],
  );

  // --- "Filter active" ⇄ L3 AI option two-way sync (D4 / S8) -----------
  // The widget switch reflects whether "ai" is in the global kind axis;
  // flipping it adds/removes "ai" from the kind axis (the single shared
  // source of truth). Identical to Track B's Defect widget.
  const active = selection.kind.includes(AI_KIND);
  const setActive = useCallback(
    (nextActive: boolean) => {
      if (nextActive === active) return;
      const next = nextActive
        ? [...selection.kind, AI_KIND]
        : selection.kind.filter((k) => k !== AI_KIND);
      setAxis("kind", next);
    },
    [active, selection.kind, setAxis],
  );

  // --- Debounced COMMIT of the aiClassFilter JSON (D8) -----------------
  // The live per-class ≥-count is debounce-free (pure histogram math
  // below). ONLY the round-trip-causing commit is debounced: when the AI
  // kind is active and the config settles, push the JSON map over the
  // checked classes into the shared store; the data-points + facets
  // queries fold it into their requests so the L3 AI count + the cascade
  // reflect the AI config. Inactive (or nothing checked) ⇒ null (no
  // narrowing).
  const commitJson = active ? buildAiClassFilterJson(config) : null;
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (commitTimer.current) clearTimeout(commitTimer.current);
    commitTimer.current = setTimeout(() => {
      setCommittedFilter(commitJson);
    }, COMMIT_DEBOUNCE_MS);
    return () => {
      if (commitTimer.current) clearTimeout(commitTimer.current);
    };
  }, [commitJson, setCommittedFilter]);

  // --- Save/Load presets + last-used persistence (Track D / D9) --------
  // The preset-compatibility key is the BE's `modelSignature` for this DA's
  // AI-model set; a saved AI config loads only against a matching signature.
  const modelSignature = data?.modelSignature ?? "";
  // Classes that currently HAVE data (validity-gating universe — D9). A
  // checked class restored from a config but absent here is dropped.
  const presentClassUuids = useMemo(() => {
    const s = new Set<string>();
    for (const it of facetItems) {
      if (it.findingCount > 0) s.add(it.classUuid);
    }
    return s;
  }, [facetItems]);

  // The live config blob the hook persists as last-used + saves as presets.
  const configBlob = useMemo(
    () =>
      buildAiConfigBlob(active, config) as unknown as Record<string, unknown>,
    [active, config],
  );

  // Apply a stored blob to the widget state, validity-gated (D9): drop
  // checked classes that no longer have data, and sync the active toggle
  // (which mirrors the L3 AI kind) to the blob's `active`.
  const applyBlob = useCallback(
    (raw: Record<string, unknown>) => {
      const blob = parseAiConfigBlob(raw);
      setConfig(gateAiConfigBlob(blob, presentClassUuids));
      setActive(blob.active);
    },
    [presentClassUuids, setConfig, setActive],
  );

  const presetsApi = useWidgetFilterConfigs({
    scope: { kind: "ai", modelSignature },
    blob: configBlob,
    facetsReady: !isPending && data !== undefined,
    applyBlob,
  });

  const extraColumn: FacetExtraColumn<AiRow> = {
    header: "Min. confidence",
    width: 230,
    render: (row) => {
      const cfg = cfgFor(row.classUuid);
      return (
        <ConfidenceControl
          classUuid={row.classUuid}
          histogram={row.histogram}
          value={cfg.minPct}
          onChange={(next) => {
            setMinPct(row.classUuid, next);
          }}
        />
      );
    },
  };

  const toolbar = (
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={showAll}
          onChange={(e) => {
            setShowAll(e.target.checked);
          }}
          data-testid="ai-filter-show-all"
          inputProps={{ "aria-label": "Show all classes" }}
        />
      }
      label="Show all classes"
      sx={{ m: 0 }}
    />
  );

  if (isError) {
    return (
      <Box
        data-testid="ai-filter-widget"
        role="region"
        aria-label="AI Filter Module"
        sx={{ p: 2 }}
      >
        <Alert severity="error">Could not load AI classes.</Alert>
      </Box>
    );
  }

  // Decorate rows with the live ≥-threshold count for the count column.
  const rowsWithLiveCount: AiRow[] = rows.map((r) => ({
    ...r,
    count: countAtOrAboveThreshold(r.histogram, cfgFor(r.classUuid).minPct),
  }));

  return (
    <FacetTableWidget<AiRow>
      ariaLabel="AI Filter Module"
      testId="ai-filter-widget"
      rows={rowsWithLiveCount}
      nameHeader="AI class"
      countHeader="# findings ≥ threshold"
      extraColumn={extraColumn}
      checkedIds={checkedIds}
      onToggleRow={toggleRow}
      onToggleAll={toggleAll}
      active={active}
      onActiveChange={setActive}
      toolbar={toolbar}
      headerSlot={
        <FilterConfigPresets
          testId="ai-filter-presets"
          presets={presetsApi.presets}
          selectedId={presetsApi.selectedId}
          disabled={presetsApi.disabled}
          busy={presetsApi.busy}
          onSelect={presetsApi.onSelect}
          onSaveAs={presetsApi.onSaveAs}
          onUpdate={presetsApi.onUpdate}
          onDelete={presetsApi.onDelete}
        />
      }
      emptyMessage={
        isPending ? (
          <Stack alignItems="center" spacing={1}>
            <CircularProgress size={20} />
          </Stack>
        ) : showAll ? (
          "No AI classes in the model(s)."
        ) : (
          "No AI classes present. Enable “Show all classes”."
        )
      }
    />
  );
}
